import React, { useState } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Button,
  Input,
  Label,
} from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import {
  CreateSchoolAndAssignProgram,
  AddClassToSchool,
  AddProgramToSchool,
  AddProgramLicenseToSchool,
  CreateNewTeacher,
  AddTeacherToClass,
} from "../../actions/superUserMutations";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { v4 as uuid } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import { GetOrientationsBySchoolId, GetAvailableProgramLicenses } from "../../actions/adminQueries";

export default function CreateNewSchoolWizardModal({ isOpen, toggle }) {
  const intl = useIntl();
  const [page, setPage] = useState(1);
  const [fortnoxNumber, setFortnoxNumber] = useState("");
  const [selectedLicenses, setSelectedLicenses] = useState([]);
  const [errors, setErrors] = useState([]);
  const [classesForPost, setClassesForPost] = useState([]);
  const [newClass, setNewClass] = useState({});
  const [createdSchool, setCreatedSchool] = useState(null);
  const [addedClasses, setAddedClasses] = useState([]);
  const [newTeacherToAdd, setNewTeacherToAdd] = useState({
    name: null,
    email: null,
    purchaser: false,
    fullAccess: false,
  });
  const [teachersForPost, setTeachersForPost] = useState([]);
  const [schoolAlreadyPosted, setSchoolAlreadyPosted] = useState(false);
  const [returnedPrograms, setReturnedPrograms] = useState([]);
  const availableLicenses = GetAvailableProgramLicenses();
  const orientations = GetOrientationsBySchoolId(createdSchool?.id);
  const handlePostSchoolWithLicense = useMutation({ mutationFn: CreateSchoolAndAssignProgram });
  const handlePostClassesToSchool = useMutation({ mutationFn: AddClassToSchool });
  const handlePostProgram = useMutation({ mutationFn: AddProgramToSchool });
  const handlePostProgramLicense = useMutation({ mutationFn: AddProgramLicenseToSchool });
  const handleCreateNewTeacher = useMutation({ mutationFn: CreateNewTeacher });
  const handleAddTeacherToClass = useMutation({ mutationFn: AddTeacherToClass });
  const queryClient = useQueryClient();

  const validateStepAndGoToNext = () => {
    switch (page) {
      case 1:
        if (fortnoxNumber.length > 0) {
          setPage(page + 1);
          setErrors(errors.filter((err) => err !== "page1"));
        } else {
          setErrors([...errors, "page1"]);
        }
        break;
      default:
        break;
    }
  };

  const handleClose = (reloadSchools) => {
    setPage(1);
    setFortnoxNumber("");
    setSelectedLicenses([]);
    setErrors([]);
    setClassesForPost([]);
    setNewClass({});
    setCreatedSchool(null);
    setNewTeacherToAdd({
      name: null,
      email: null,
      purchaser: false,
      fullAccess: false,
    });
    setTeachersForPost([]);
    setAddedClasses([]);
    setSchoolAlreadyPosted(false);
    setReturnedPrograms([]);
    toggle();
    if (reloadSchools) {
      queryClient.invalidateQueries({ queryKey: ["FetchSchoolsForAdmin"] });
    }
  };

  const postSchoolWithLicenses = (saveAndClose) => {
    let schoolData = null;
    if (!schoolAlreadyPosted) {
      handlePostSchoolWithLicense.mutate(
        {
          fortKnoxCustomerNumber: fortnoxNumber,
          licenses: selectedLicenses,
        },
        {
          onSuccess: (data) => {
            if (data && data.status > 199 && data.status < 300) {
              schoolData = data.data.result;
              toast.success(<FormattedMessage id="SchoolCreated" />);
              setCreatedSchool(data.data.result);
              setSchoolAlreadyPosted(true);
              postLicenses(schoolData, saveAndClose);
            } else {
              toast.warn(data.response.data);
            }
          },
          throwOnError: true,
          onError: (err) => {
            toast.warn(err);
          },
        }
      );
    } else {
      postLicenses(createdSchool, saveAndClose);
    }
  };

  const postLicenses = (schoolData, saveAndClose) => {
    selectedLicenses.forEach((lic) => {
      handlePostProgramLicense.mutate(
        {
          license: lic,
          school: schoolData,
        },
        {
          onSuccess: async (proglicData) => {
            if (
              proglicData &&
              proglicData.status > 199 &&
              proglicData.status < 300
            ) {
              toast.success(<FormattedMessage id="LicenseAdded" />);

              const promises = [];
              selectedLicenses.forEach((proglic) => {
                promises.push(
                  handlePostProgram.mutateAsync({
                    license: proglic,
                    school: schoolData,
                  })
                );
              });
              const responses = await Promise.all(promises);
              const tempPrograms = [];
              responses.forEach((resp) => {
                if (resp && resp.status > 199 && resp.status < 300) {
                  tempPrograms.push(resp.data.result);
                } else {
                  toast.warn(resp.response.data);
                }
              });
              toast.success(<FormattedMessage id="ProgramAdded" />);
              setReturnedPrograms(tempPrograms);
            } else {
              toast.warn(proglicData.response.data);
            }
          },
          onError: (proglicError) => {
            toast.warn(proglicError.response.data);
          },
        }
      );
    });
    saveAndClose ? handleClose(true) : setPage(page + 1);
  };

  const handlePostClasses = async () => {
    const promises = [];
    classesForPost.forEach((cl) => {
      const program = {
        id: cl.program.id,
        name: cl.program.name,
        costCenter: cl.program.costCenter,
        customer: createdSchool,
        classes: null,
      };
      promises.push(
        handlePostClassesToSchool.mutateAsync({
          name: cl.name,
          school: createdSchool,
          selectedProgramme: program,
          selectedOrientation:
            cl.orientation !== undefined ? cl.orientation : "",
        })
      );
    });
    const responses = await Promise.all(promises);
    const tempClasses = [];
    responses.forEach((resp) => {
      if (resp && resp.status > 199 && resp.status < 300) {
        tempClasses.push(resp.data.result);
      } else {
        toast.warn(resp.response.data);
      }
    });
    toast.success(<FormattedMessage id="ClassesAdded" />);
    setAddedClasses(tempClasses);

    setPage(page + 1);
  };

  const handlePostTeachersToClass = (postedTeachers, teachersForPost) => {
    postedTeachers.forEach((pTeacher) => {
      handleAddTeacherToClass.mutate(
        {
          teacherId: pTeacher.id,
          classIds: teachersForPost
            .find((t) => t.email === pTeacher.email)
            .classes.map((a) => a.value.id),
        },
        {
          onSuccess: (data) => {
            if (data && data.status > 199 && data.status < 300) {
              toast.success(<FormattedMessage id="TeacherAddedToClass" />);
            } else {
              toast.warn(data.response.data);
            }
          },
        }
      );
    });
    handleClose(true);
  };

  const handlePostTeachersAndClose = async () => {
    const postedTeachers = [];
    const promises = [];
    teachersForPost.forEach((teacher) => {
      const teacherToPost = {
        name: teacher.name,
        email: teacher.email,
        fullAccess: teacher.fullAccess,
        purchaser: teacher.purchaser,
        activatedAccount: false,
        aplSupervisorMail: null,
        classes: teacher.classes,
      };
      promises.push(handleCreateNewTeacher.mutateAsync({ teacherToPost }));
    });
    const responses = await Promise.all(promises);
    responses.forEach((resp) => {
      if (resp && resp.status > 199 && resp.status < 300) {
        postedTeachers.push(resp.data.result);
      } else {
        toast.warn(resp.response.data);
      }
    });
    toast.success(<FormattedMessage id="TeacherAdded" />);
    handlePostTeachersToClass(postedTeachers, teachersForPost);
  };

  const renderPage = () => {
    switch (page) {
      case 1:
        return fortnoxNumberPage();
      case 2:
        return licensesPage();
      case 3:
        return selectedLicensescreateClasses();
      case 4:
        return connectTeachersToClasses();
      default:
        return fortnoxNumberPage();
    }
  };

  const handleSetFortnoxNumber = (number) => {
    if (number.length > 0) {
      setErrors(errors.filter((err) => err !== "page1"));
    }
    setFortnoxNumber(number);
  };

  const fortnoxNumberPage = () => {
    return (
      <>
        <p className="lead">
          <FormattedMessage id="StateFortnoxNumber" />
        </p>
        <Row>
          <Col>
            <Label for="fortnoxNumber">
              <FormattedMessage id="FortnoxNumber" />
            </Label>
          </Col>
          <Col>
            <Input
              type="text"
              style={{
                border: errors.includes("page1")
                  ? "1px solid red"
                  : "1px solid black",
              }}
              name="fortnoxNumber"
              value={fortnoxNumber}
              onChange={(event) => handleSetFortnoxNumber(event.target.value)}
            ></Input>
          </Col>
        </Row>
      </>
    );
  };

  const licensesPage = () => {
    return (
      <>
        <p className="lead">
          <FormattedMessage id="AddProgramLicenses" />
        </p>
        <Label for="licenseSelector">
          <FormattedMessage id="ChooseProgramLicensesToAdd" />
        </Label>
        <div>
          <FormattedMessage id="NoteThatSchoolWillGetLicenseAndProgram" />.
        </div>
        <Select
          isMulti={true}
          name="licenseSelector"
          options={(availableLicenses.data ?? []).map((option) => ({
            ...option,
            label: option.displayName,
            value: option,
            clearableValue: true,
          }))}
          onChange={(data) => setSelectedLicenses(data)}
        ></Select>
      </>
    );
  };

  const selectedLicensescreateClasses = () => {
    return (
      <>
        <p className="lead">
          <FormattedMessage id="AddClasses" />
        </p>
        <Row className="pt-2">
          <Col lg="2">
            <Label for="classNameForCreate">
              <FormattedMessage id="ClassName" />
            </Label>
          </Col>
          <Col>
            <Input
              name="classNameForCreate"
              type="text"
              value={newClass.name}
              onChange={(data) =>
                setNewClass({ ...newClass, name: data.target.value })
              }
            ></Input>
          </Col>
        </Row>
        <Row className="pt-2">
          <Col lg="2">
            <Label for="programForClass">
              <FormattedMessage id="Program" />
            </Label>
          </Col>
          <Col>
            <Select
              isMulti={false}
              name="programForClass"
              options={returnedPrograms.map((option) => ({
                ...option,
                label: option.name,
                value: option,
                clearableValue: true,
              }))}
              onChange={(data) => setNewClass({ ...newClass, program: data })}
            ></Select>
          </Col>
        </Row>
        <Row className="pt-2">
          <Col lg="2">
            <Label for="orientationForClass">
              <FormattedMessage id="Orientation" />
            </Label>
          </Col>
          <Col>
            <CreatableSelect
              name="selectedOrientation"
              isClearable
              placeholder={intl.formatMessage({ id: "ChooseOrientationOrCreate" })}
              noOptionsMessage={() => intl.formatMessage({ id: "NoAvailablePrograms" })}
              onChange={(e) =>
                e
                  ? setNewClass({ ...newClass, orientation: e.label })
                  : setNewClass({ ...newClass, orientation: null })
              }
              options={(orientations.data ?? []).map((option) => ({
                ...option,
                label: option.name,
                value: option.id.toString(),
                clearableValue: true,
              }))}
              formatCreateLabel={(userInput) =>
                intl.formatMessage({ id: "ClickHereToCreate" }, { userInput })
              }
            />
          </Col>
        </Row>
        <Row className="pt-2">
          <Col>
            <Button
              onClick={() => selectedLicenses.length > 0 && handleAddClass()}
              title={
                selectedLicenses.length < 1 && (
                  <FormattedMessage id="YouCannotAddAClassIfYouHaveNotAddedProgram" />
                )
              }
              outline
              color="primary"
              style={{
                cursor: selectedLicenses.length < 1 ? "not-allowed" : "pointer",
              }}
            >
              <FormattedMessage id="AddClass" />
            </Button>
          </Col>
        </Row>
        {classesForPost.length > 0 && (
          <>
            <Row>
              <Col lg="1"></Col>
              <Col>
                <FormattedMessage id="Name" />
              </Col>
              <Col>
                <FormattedMessage id="Program" />
              </Col>
              <Col>
                <FormattedMessage id="Orientation" />
              </Col>
            </Row>
            {classesForPost.map((cl) => (
              <Row key={cl.id}>
                <Col lg="1">
                  <Button
                    color="danger"
                    outline={true}
                    size="sm"
                    onClick={() => handleRemoveClassFromAdd(cl)}
                  >
                    <FontAwesomeIcon icon="trash" />
                  </Button>
                </Col>
                <Col>{cl.name}</Col>
                <Col>{cl.program.name}</Col>
                <Col>{cl.orientation}</Col>
              </Row>
            ))}
          </>
        )}
      </>
    );
  };

  const connectTeachersToClasses = () => {
    return (
      <>
        <p className="lead">
          <FormattedMessage id="AddTeacher" />
        </p>
        <Row className="mt-2">
          <Col>
            <Label for="newTeacherName">
              <FormattedMessage id="Name" />
            </Label>
          </Col>
          <Col>
            <Label for="newTeacherEmail">
              <FormattedMessage id="Email" />
            </Label>
          </Col>
        </Row>
        <Row>
          <Col>
            <Input
              value={newTeacherToAdd.name}
              onChange={(data) =>
                setNewTeacherToAdd({
                  ...newTeacherToAdd,
                  name: data.target.value,
                })
              }
              type="text"
              placeholder={intl.formatMessage({ id: "Name" })}
              name="newTeacherName"
              style={
                errors.includes("teacherName")
                  ? { borderColor: "red" }
                  : { borderColor: "#c2cfd6" }
              }
            />
          </Col>

          <Col>
            <Input
              value={newTeacherToAdd.email}
              onChange={(data) =>
                setNewTeacherToAdd({
                  ...newTeacherToAdd,
                  email: data.target.value,
                })
              }
              type="text"
              placeholder="Epost"
              name="newTeacherEmail"
              style={
                errors.includes("teacherEmail")
                  ? { borderColor: "red" }
                  : { borderColor: "#c2cfd6" }
              }
            />
          </Col>
        </Row>
        <Row className="mt-1">
          <Col style={{ marginLeft: "1.5em" }}>
            <Input
              value={newTeacherToAdd.purchaser}
              onChange={(data) =>
                setNewTeacherToAdd({
                  ...newTeacherToAdd,
                  purchaser: data.target.checked,
                })
              }
              name="purchaserCheckBox"
              type="checkbox"
            />
            <Label for="purchaserCheckBox">
              <FormattedMessage id="Purchaser" />
            </Label>
          </Col>
        </Row>
        <Row>
          <Col style={{ marginLeft: "1.5em" }}>
            <Input
              value={newTeacherToAdd.fullAccess}
              onChange={(data) =>
                setNewTeacherToAdd({
                  ...newTeacherToAdd,
                  fullAccess: data.target.checked,
                })
              }
              name="fullAccessCheckbox"
              type="checkbox"
            />
            <Label for="fullAccessCheckbox">
              <FormattedMessage id="FullTeacherAccess" />
            </Label>
          </Col>
        </Row>
        <Row>
          <Col>
            <Button
              onClick={() => handleCheckAndSetTeacherForAdd()}
              color="primary"
              outline
            >
              <FormattedMessage id="SaveTeacher" />
            </Button>
          </Col>
        </Row>
        <Row>
          <Col lg="1"></Col>
          <Col lg="3">
            <FormattedMessage id="Name" />
          </Col>
          <Col lg="3">
            <FormattedMessage id="Email" />
          </Col>
          <Col lg="5">
            <FormattedMessage id="Classes" />
          </Col>
        </Row>
        {teachersForPost &&
          teachersForPost.map((teacher) => {
            let selectedClasses = [];
            teacher.classes.forEach((cl) => {
              selectedClasses.push({
                label: cl.value.name + " -- " + cl.value.programme.name,
                value: cl.value,
              });
            });

            const options = [];
            addedClasses.forEach((cl) => {
              options.push({
                label: cl.name + " -- " + cl.programme.name,
                value: cl,
                clearableValue: true,
              });
            });
            return (
              <Row key={teacher.id}>
                <Col lg="1">
                  <Button
                    color="danger"
                    outline={true}
                    size="sm"
                    onClick={() => handleRemoveTeacherFromAdd(teacher)}
                  >
                    <FontAwesomeIcon icon="trash" />
                  </Button>
                </Col>
                <Col lg="3">
                  <p style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {teacher.name}
                  </p>
                </Col>
                <Col lg="3">
                  <p style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {teacher.email}
                  </p>
                </Col>
                <Col lg="5">
                  <Select
                    isMulti={true}
                    name="programForClass"
                    value={selectedClasses}
                    options={options}
                    onChange={(data) =>
                      handleSetClassForNewTeacher(data, teacher)
                    }
                  ></Select>
                </Col>
              </Row>
            );
          })}
      </>
    );
  };

  const handleCheckAndSetTeacherForAdd = () => {
    var pattern = /^[\w.-]+@[\w.-]+\.\w+$/;
    if (!pattern.test(newTeacherToAdd.email)) {
      setErrors([...errors, "teacherEmail"]);
      return null;
    }
    if (newTeacherToAdd.name.length < 1) {
      setErrors([...errors, "teacherName"]);
      return null;
    }
    var excludedValues = ["teacherName", "teacherEmail"];

    var tempErrors = errors.filter(function (value) {
      return !excludedValues.includes(value);
    });
    setErrors(tempErrors);
    setTeachersForPost([
      ...teachersForPost,
      {
        id: uuid(),
        name: newTeacherToAdd.name,
        email: newTeacherToAdd.email,
        purchaser: newTeacherToAdd.purchaser,
        fullAccess: newTeacherToAdd.fullAccess,
        classes: [],
      },
    ]);
  };

  const handleRemoveTeacherFromAdd = (teacher) => {
    let tempTeachers = [...teachersForPost];
    tempTeachers.splice(
      tempTeachers.findIndex((t) => t.id === teacher.id),
      1
    );
    setTeachersForPost(tempTeachers);
  };

  const handleRemoveClassFromAdd = (cl) => {
    let tempClasses = [...classesForPost];
    tempClasses.splice(
      tempClasses.findIndex((t) => t.id === cl.id),
      1
    );
    setClassesForPost(tempClasses);
  };

  const handleSetClassForNewTeacher = (cl, teacher) => {
    let tempTeachers = [...teachersForPost];
    tempTeachers.find((t) => t.id === teacher.id).classes = cl;
    setTeachersForPost(tempTeachers);
  };

  const handleAddClass = () => {
    setClassesForPost([...classesForPost, { ...newClass, id: uuid() }]);
  };

  return (
    <Modal size="lg" isOpen={isOpen} toggle={() => handleClose()}>
      <ModalBody>{renderPage()}</ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={() => handleClose()}>
          <FormattedMessage id="Cancel" />
        </Button>
        {page === 2 && (
          <Button
            color="primary"
            outline
            onClick={() => postSchoolWithLicenses(true)}
          >
            <FormattedMessage id="SaveAndClose" />
          </Button>
        )}
        {page >= 2 && (
          <Button
            color="primary"
            outline
            onClick={() => (page >= 2 ? setPage(page - 1) : null)}
          >
            <FormattedMessage id="GoBack" />
          </Button>
        )}
        {page === 2 ? (
          <Button onClick={() => postSchoolWithLicenses(false)}>
            <FormattedMessage id="SaveAndContinue" />
          </Button>
        ) : page === 3 ? (
          <Button color="primary" outline onClick={() => handlePostClasses()}>
            <FormattedMessage id="SaveAndContinue" />
          </Button>
        ) : page === 4 ? (
          <Button
            color="primary"
            outline
            onClick={() => handlePostTeachersAndClose()}
          >
            <FormattedMessage id="SaveAndClose" />
          </Button>
        ) : (
          <Button
            color="primary"
            outline
            onClick={() => validateStepAndGoToNext()}
          >
            <FormattedMessage id="Next" />
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
